"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REPRICE_PARTS = exports.paintLabel = exports.priceTag = exports.oneOffPaints = exports.jobMaterials = exports.quotedHire = exports.oneOffPaintId = exports.oneOffPaintKey = exports.JOB_PRICE_KIND = exports.JOB_PRICE_TABLE = void 0;
exports.jobPricesOf = jobPricesOf;
exports.jobPriceForProduct = jobPriceForProduct;
exports.jobPriceForHire = jobPriceForHire;
exports.repriceCheck = repriceCheck;
/** The device table the rows sync as. */
exports.JOB_PRICE_TABLE = 'jobprice';
exports.JOB_PRICE_KIND = { paint: 'Paint', hire: 'Hire', material: 'Material/trade' };
/** A one-off paint's key in an option's paint list: this prefix, then its job price row's id. */
const ONE_OFF_PAINT_PREFIX = 'job:';
const text = (value) => (typeof value === 'string' && value ? value : null);
const amount = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
/** The measure's job price rows, as the device holds them. */
function jobPricesOf(records, measureId) {
    return records
        .filter((r) => r.table === exports.JOB_PRICE_TABLE && r.parentId === measureId)
        .map((r) => ({
        id: r.id,
        kind: text(r.fields.hig_kind),
        name: text(r.fields.hig_name) ?? '',
        paintProductId: text(r.fields.hig_paintproductid),
        hireItemId: text(r.fields.hig_hireitemid),
        costPerLitre: amount(r.fields.hig_costperlitre),
        dayRate: amount(r.fields.hig_dayrate),
        transport: amount(r.fields.hig_transport),
        diesel: r.fields.hig_diesel === true,
        rate: amount(r.fields.hig_rate),
        bookPrice: amount(r.fields.hig_bookprice),
        bookDate: text(r.fields.hig_bookdate),
    }));
}
const oneOffPaintKey = (jobPriceId) => `${ONE_OFF_PAINT_PREFIX}${jobPriceId}`;
exports.oneOffPaintKey = oneOffPaintKey;
/** The job price row a paint key names, when it is a one-off paint's; null for any other paint. */
const oneOffPaintId = (key) => (key.startsWith(ONE_OFF_PAINT_PREFIX) ? key.slice(ONE_OFF_PAINT_PREFIX.length) : null);
exports.oneOffPaintId = oneOffPaintId;
/** The job price typed for a price-book product, when one is (ids compared without case). */
function jobPriceForProduct(prices, productId) {
    const id = productId.toLowerCase();
    return prices.find((p) => p.paintProductId?.toLowerCase() === id && p.costPerLitre !== null);
}
/** The job price typed for a hire-list item, when one is. */
function jobPriceForHire(prices, hireItemId) {
    const id = hireItemId.toLowerCase();
    return prices.find((p) => p.hireItemId?.toLowerCase() === id && (p.dayRate !== null || p.transport !== null));
}
/** The job's quoted hire items: hire rows with no hire-list item. */
const quotedHire = (prices) => prices.filter((p) => p.kind === exports.JOB_PRICE_KIND.hire && !p.hireItemId);
exports.quotedHire = quotedHire;
/** The job's materials and trades. */
const jobMaterials = (prices) => prices.filter((p) => p.kind === exports.JOB_PRICE_KIND.material);
exports.jobMaterials = jobMaterials;
/** The job's one-off paints: paint rows with no price-book product. */
const oneOffPaints = (prices) => prices.filter((p) => p.kind === exports.JOB_PRICE_KIND.paint && !p.paintProductId);
exports.oneOffPaints = oneOffPaints;
/** How a paint's price is marked for the reviewer (#413 decision 3), wherever the quote is shown. */
const PRICE_TAGS = { job: 'job price', oneOff: 'one-off' };
/** A paint's job-price or one-off tag; null for a book or typed price. */
const priceTag = (paint) => (paint.priceSource ? (PRICE_TAGS[paint.priceSource] ?? null) : null);
exports.priceTag = priceTag;
/** A paint's name with its tag, as the Take Off, the Summary and the REVIEW show it. */
const paintLabel = (paint) => {
    const tag = (0, exports.priceTag)(paint);
    return tag ? `${paint.name} · ${tag}` : paint.name;
};
exports.paintLabel = paintLabel;
exports.REPRICE_PARTS = ['price', 'dayRate', 'transport'];
/** A job price row's prices as reprice lines: one for a paint or material, one per typed half (day rate, cartage) for hire. */
function repriceParts(p) {
    if (p.kind === exports.JOB_PRICE_KIND.hire) {
        return [
            ...(p.dayRate !== null ? [{ part: 'dayRate', price: p.dayRate }] : []),
            ...(p.transport !== null ? [{ part: 'transport', price: p.transport }] : []),
        ];
    }
    const price = p.costPerLitre ?? p.rate;
    return price !== null ? [{ part: 'price', price }] : [];
}
/** Every job price, against today's book where it replaced one (DD 2026-10-22: Keep or Use book price). */
function repriceCheck(prices, book) {
    return prices.flatMap((p) => repriceParts(p).map(({ part, price }) => {
        const inBook = Boolean(p.paintProductId || p.hireItemId);
        const hire = p.hireItemId ? book.hire(p.hireItemId) : null;
        const bookNow = p.paintProductId ? book.paint(p.paintProductId) : hire ? (part === 'transport' ? hire.transport : hire.dayRate) : null;
        // The recorded book price is the paint's $/L or the hire item's day rate; cartage has none recorded.
        const recorded = inBook && part !== 'transport';
        return { id: p.id, part, name: p.name, kind: p.kind, jobPrice: price, bookThen: recorded ? p.bookPrice : null, bookThenDate: recorded ? p.bookDate : null, bookNow, inBook };
    }));
}
